import React, { FormEvent, useEffect, useState } from 'react';
import Appbar from 'components/appbar/Appbar';
import { Chip } from '@mui/material';
import { moneyFormat } from 'helpers/moneyFormat';
import ProcedureForm from '../form/ProcedureForm';
import { useProcedureValidation } from '../validation/useProcedureValidation';
import { api } from 'services/api';
import { toast } from 'react-toastify';
import history from 'services/history';
import InsideSaving from 'components/loading/InsideSaving';
import { ProcedureDoctor, ProcedureSpecialty } from 'types/procedure';
import { Procedure } from 'types/procedure';
import ProcedureTab from '../tab/ProcedureTab';
import ProcedureDoctors from '../ProcedureDoctors';
import ProcedureSpecialtiesForm from '../form/ProcedureSpecialtiesForm';
import { Specialty } from 'types/specialty';
import {
  ContentPad,
  PageShell,
  RecordActions,
  RecordHeader,
  RecordNote,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';

const NewProcedure: React.FC = () => {
  const [validation, setValidation, validate] = useProcedureValidation();
  const [tab, setTab] = useState('main');
  const [saving, setSaving] = useState(false);
  const [procedure, setProcedure] = useState<Procedure>({
    id: 0,
    name: '',
    anesthetist: null,
    active: true,
    doctors: [],
    packages: [],
    products: [],
    specialties: [],
    profit_margin: 0,
    sale_price: 0,
    commission: 0,
    cost: 0,
    type: 'G',
    sum_commission_to_total: false,
  });
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);

  useEffect(() => {
    setLoadingSpecialties(true);

    api
      .get('/specialties/all')
      .then(response => {
        setSpecialties(response.data);
      })
      .catch(err => {
        console.error(err);
        toast.error('Não foi possível carregar as especialidades');
      })
      .finally(() => {
        setLoadingSpecialties(false);
      });
  }, []);

  function handleChangeSpecialties(selectedIds: number[]) {
    const updatedSpecialties: ProcedureSpecialty[] = specialties.map(specialty => ({
      specialty_id: specialty.id,
      active: selectedIds.includes(specialty.id) ? 1 : 0,
      id: specialty.id,
      specialty_name: specialty.name,
      specialty: specialty,
    }));
    setProcedure(prev => ({ ...prev, specialties: updatedSpecialties }));
  }

  function handleChange<K extends keyof Procedure>(index: K, value: Procedure[K]) {
    setProcedure(prev => ({ ...prev, [index]: value }));
  }

  function handleSubmit() {
    setSaving(true);

    api
      .post('/procedures', { ...procedure })
      .then(() => {
        toast.success('Consulta cadastrada com sucesso!');
        history.push('/consultation');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível cadastrar a consulta');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    validate(procedure)
      .then(() => {
        handleSubmit();
        setValidation({});
      })
      .catch(() => {
        // o erro já foi guardado em `validation`
      });
  }

  // nome e preço ficam na aba Geral: abre ela quando a validação reclama de um deles
  useEffect(() => {
    if (Object.keys(validation).length) setTab('main');
  }, [validation]);

  function handleChangeAddDoctor(doctor: ProcedureDoctor) {
    setProcedure(prev => {
      const existingDoctor = prev.doctors.find(b => b.id === doctor.id);
      if (existingDoctor) {
        return {
          ...prev,
          doctors: prev.doctors.map(b => (b.id === doctor.id ? { ...b, ...doctor } : b)),
        };
      }
      return {
        ...prev,
        doctors: [
          ...prev.doctors,
          { ...doctor, id: prev.doctors.length ? Math.max(...prev.doctors.map(b => b.id)) + 1 : 1 },
        ],
      };
    });
  }

  function handleDeleteDoctor(doctor: ProcedureDoctor) {
    setProcedure(prev => ({
      ...prev,
      doctors: prev.doctors.filter(b => b.id !== doctor.id),
    }));
  }

  const counts = {
    doctors: procedure.doctors.length,
    specialties: procedure.specialties.filter(item => item.active !== 0).length,
  };

  function goBack() {
    history.push('/consultation');
  }

  return (
    <>
      <Appbar title="Consultas" backAction={goBack} />

      {saving && <InsideSaving />}

      <PageShell>
        <RecordHeader title="Nova consulta" backAction={goBack}>
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            label={`Preço ${moneyFormat(procedure.sale_price || 0)}`}
          />
        </RecordHeader>

        <WorkArea>
          <ProcedureTab onChange={newTab => setTab(newTab)} tab={tab} counts={counts} />
          <ContentPad>
            <StyledForm onSubmit={handleValidation}>
              {tab === 'doctors' ? (
                <ProcedureDoctors
                  procedure={procedure}
                  handleDeleteDoctor={handleDeleteDoctor}
                  handleChangeAddDoctor={handleChangeAddDoctor}
                />
              ) : tab === 'specialties' ? (
                <ProcedureSpecialtiesForm
                  handleChangeSpecialties={handleChangeSpecialties}
                  procedure={procedure}
                  loadingSpecialties={loadingSpecialties}
                  specialties={specialties}
                />
              ) : (
                <ProcedureForm handleChange={handleChange} procedure={procedure} validation={validation} />
              )}
              {tab === 'doctors' && <RecordNote>Os médicos são salvos junto com a consulta.</RecordNote>}
              <button type="submit" style={{ display: 'none' }}></button>
            </StyledForm>
          </ContentPad>
        </WorkArea>

        <RecordActions saving={saving} saveLabel="Salvar consulta" onSave={() => handleValidation()} />
      </PageShell>
    </>
  );
};

export default NewProcedure;
